// Sprint TENANTS TENANTS.B3 (#2339) — audit trail tab content.
//
// Backend: GET /api/internal/tenants/:id/audit, added AS PART OF this story
// (recon found no such endpoint existed anywhere before #2339 — TENANTS.A3
// wrote audit rows correctly but nothing read them back). See
// internal-tenants.router.ts's own comment on why it filters against
// TENANT_ADMIN_AUDIT_ACTIONS rather than a bare tenant_id match.
//
// Lazy-loaded: this component only mounts (and therefore only fetches)
// once the Audit Trail tab is first selected in TenantExpandedRow — see
// that file's tab-switching logic. Avoids firing a third network request
// on every row expand for a tab most admins will never open, and re-fetches
// fresh data each time the tab is re-selected (same mount/unmount-driven
// freshness as the whole expanded row already has on collapse/re-expand).

import { useCallback, useEffect, useState } from 'react';
import { apiFetch, ApiError } from '../../lib/api';

interface AuditAction {
  id: string;
  action: string;
  actor_id: string | null;
  actor_name: string | null;
  reason: string | null;
  before: unknown;
  after: unknown;
  timestamp: string | null;
}

interface AuditResponse {
  actions: AuditAction[];
}

const ACTION_LABELS: Record<string, string> = {
  TENANT_SUSPENDED_IMMEDIATE:          'Suspended (immediate)',
  TENANT_SUSPENDED_GRACEFUL_SCHEDULED: 'Suspension scheduled (graceful)',
  TENANT_REACTIVATED:                  'Reactivated',
  TENANT_TIER_CHANGED:                 'Tier changed',
  TENANT_SEAT_LIMIT_CHANGED:           'Seat limit changed',
  TENANT_FEATURE_FLAG_UPDATED:         'Feature flag updated',
};

function fmtTimestamp(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

// `before`/`after` are single-key objects for every action this endpoint
// returns (e.g. {subscriptionTier: 'STARTER'}) — rendered as "key: value"
// rather than raw JSON, matching the "old → new value" wording in the story.
function fmtValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'object') {
    const entries = Object.entries(v as Record<string, unknown>);
    if (entries.length === 0) return '—';
    return entries.map(([k, val]) => `${k}: ${String(val)}`).join(', ');
  }
  return String(v);
}

function errorMessage(err: unknown, fallback: string): string {
  const e = err as ApiError | null;
  return (e?.body?.error as string | undefined) ?? fallback;
}

const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const thStyle: React.CSSProperties = { textAlign: 'left', padding: '6px 10px', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', borderBottom: '1px solid var(--border)' };
const tdStyle: React.CSSProperties = { padding: '6px 10px', borderBottom: '1px solid var(--border)', color: 'var(--text)', verticalAlign: 'top' };

function skeletonBar(height = 14): React.CSSProperties {
  return { height, borderRadius: 4, background: 'var(--surface-alt)', marginBottom: 6 };
}

interface Props {
  tenantId: string;
}

export default function TenantAuditTab({ tenantId }: Props) {
  const [actions, setActions] = useState<AuditAction[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  const fetchAudit = useCallback(async (isStale: () => boolean) => {
    setLoading(true);
    setError('');
    try {
      const res = await apiFetch<AuditResponse>(`/api/internal/tenants/${encodeURIComponent(tenantId)}/audit`);
      if (isStale()) return;
      setActions(res.actions ?? []);
    } catch (err) {
      if (isStale()) return;
      setError(errorMessage(err, 'Failed to load audit trail.'));
    } finally {
      if (!isStale()) setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    let cancelled = false;
    fetchAudit(() => cancelled);
    return () => { cancelled = true; };
  }, [fetchAudit]);

  if (loading) {
    return (
      <div data-testid={`tenant-audit-loading-${tenantId}`}>
        <div style={skeletonBar()} />
        <div style={skeletonBar()} />
        <div style={skeletonBar()} />
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid={`tenant-audit-error-${tenantId}`} style={{ fontSize: 13, color: 'var(--error)' }}>
        {error}
      </div>
    );
  }

  if (!actions || actions.length === 0) {
    return (
      <div data-testid={`tenant-audit-empty-${tenantId}`} style={{ fontSize: 13, color: 'var(--text-muted)' }}>
        No admin actions recorded for this tenant yet.
      </div>
    );
  }

  return (
    <table style={tableStyle} data-testid={`tenant-audit-table-${tenantId}`}>
      <thead>
        <tr>
          <th style={thStyle}>Action</th>
          <th style={thStyle}>Performed by</th>
          <th style={thStyle}>Timestamp</th>
          <th style={thStyle}>Reason</th>
          <th style={thStyle}>Old → New</th>
        </tr>
      </thead>
      <tbody>
        {actions.map(a => (
          <tr key={a.id} data-testid={`tenant-audit-row-${a.id}`}>
            <td style={tdStyle}>{ACTION_LABELS[a.action] ?? a.action}</td>
            {/* "name or UUID" per the story — actor_name is null when the
                acting user's own row is gone (hard-delete cascade), not an
                error state. */}
            <td style={tdStyle} data-testid={`tenant-audit-actor-${a.id}`}>{a.actor_name ?? a.actor_id ?? '—'}</td>
            <td style={tdStyle}>{fmtTimestamp(a.timestamp)}</td>
            <td style={tdStyle}>{a.reason ?? '—'}</td>
            <td style={tdStyle}>{fmtValue(a.before)} → {fmtValue(a.after)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
